import type { DepositBonusOption, Pence } from '@/types'

/**
 * Coin scaling constants for deposit options
 * These values control the visual coin stack representation across bundles and wallet top-ups
 */
export const MIN_BUNDLE_COINS = 2 // Minimum coins shown for smallest bundle
export const MAX_BUNDLE_COINS = 7 // Maximum coins shown for largest bundle
export const MAX_WALLET_COINS = 15 // Maximum coins shown for wallet amounts above bundle range
export const WALLET_EXTRA_RANGE = MAX_WALLET_COINS - MAX_BUNDLE_COINS // Extended range (13) for wallet scaling
function multiplierToBonusPercent(multiplier: number): number {
  if (!Number.isFinite(multiplier) || multiplier <= 0) {
    return 0
  }
  const percent = multiplier >= 1 ? multiplier - 1 : multiplier
  return Math.max(0, percent)
}

/**
 * Calculate bonus credit percentage for a given amount based on bonus tiers
 */
export function calculateBonusCredit(
  amount: Pence,
  customWalletOption: DepositBonusOption | null,
  depositAmounts: Map<string, DepositBonusOption>
): number {
  // First check if we have a custom wallet product with bonus_tiers
  if (customWalletOption?.components?.[0]?.payload?.bonus_tiers) {
    const tiers = customWalletOption.components[0].payload.bonus_tiers
    // Sort tiers descending by min_amount to find the highest applicable tier
    const sortedTiers = [...tiers].sort((a, b) => b.min_amount - a.min_amount)

    for (const tier of sortedTiers) {
      if (amount >= tier.min_amount) {
        return multiplierToBonusPercent(tier.bonus_multiplier)
      }
    }
  }

  // Fallback: check fixed-price bundles
  const descDepositAmounts = Array.from(depositAmounts.values())
    .filter((opt) => opt.price_minor !== null)
    .sort((a, b) => (b.price_minor ?? 0) - (a.price_minor ?? 0))

  for (const depositOption of descDepositAmounts) {
    if (amount >= (depositOption.price_minor ?? 0)) {
      // Extract bonus as decimal from components or metadata
      const bonusMultiplier =
        depositOption.components?.[0]?.payload?.bonus_multiplier ??
        depositOption.metadata?.bonus_multiplier ??
        1
      return multiplierToBonusPercent(bonusMultiplier)
    }
  }
  return 0
}

/**
 * Calculate bonus tier information for a given amount in pounds
 */
export function getBonusTierForAmount(
  pounds: number,
  customWalletOption: DepositBonusOption | null
): {
  percent: number
  nextTier: number | null
  remainingPence: number
  nextBonusPercent: number
} {
  const pence = pounds * 100

  if (customWalletOption?.components?.[0]?.payload?.bonus_tiers) {
    const tiers = customWalletOption.components[0].payload.bonus_tiers
    const sortedTiers = [...tiers].sort((a, b) => b.min_amount - a.min_amount)

    let currentTier = null
    let nextTier = null

    for (let i = 0; i < sortedTiers.length; i++) {
      if (pence >= sortedTiers[i].min_amount) {
        currentTier = sortedTiers[i]
        break
      }
      nextTier = sortedTiers[i]
    }

    if (currentTier) {
      const bonusPercent = multiplierToBonusPercent(
        currentTier.bonus_multiplier
      )

      // Calculate remaining amount needed for next tier
      const remainingPence = nextTier
        ? Math.max(0, nextTier.min_amount - pence)
        : 0
      const nextBonusPercent = nextTier
        ? multiplierToBonusPercent(nextTier.bonus_multiplier)
        : 0

      return {
        percent: bonusPercent,
        nextTier: nextTier ? Math.ceil(nextTier.min_amount / 100) : null,
        remainingPence,
        nextBonusPercent,
      }
    }
  }

  return { percent: 0, nextTier: null, remainingPence: 0, nextBonusPercent: 0 }
}

/**
 * Calculate coin counts for custom wallet amounts
 * Uses proportional scaling for amounts up to bundle max, then scales to max wallet coins
 * Returns total coins and silver bonus coins
 */
export function calculateWalletCoins(
  amountInPence: number,
  bonusPercent: number,
  maxAmount: number,
  optionAmounts: number[] // amounts from visible options for scaling consistency
): { totalCoins: number; silverCoins: number } {
  if (amountInPence < 1) {
    return { totalCoins: 1, silverCoins: 0 }
  }

  const optionMax = Math.max(...optionAmounts)

  // For amounts up to the highest bundle, use proportional scaling (same as bundle cards)
  if (amountInPence <= optionMax) {
    // Proportional scaling: price ratio × max bundle coins
    const totalCoins = Math.max(
      MIN_BUNDLE_COINS,
      Math.round((amountInPence / optionMax) * MAX_BUNDLE_COINS)
    )
    const silverCoins = Math.min(
      totalCoins - 1,
      Math.ceil(totalCoins * bonusPercent)
    )
    return { totalCoins, silverCoins }
  }

  /**
   * For amounts above the highest bundle, scale from MAX_BUNDLE_COINS → MAX_WALLET_COINS
   * This provides visual feedback when users enter custom amounts exceeding preset bundles,
   * while staying within the API-defined wallet maximum. The extended range (13 coins)
   * gives users clear differentiation between standard bundles and higher custom deposits.
   */
  const normalized =
    maxAmount === optionMax
      ? 1
      : Math.min(
          1,
          Math.max(0, (amountInPence - optionMax) / (maxAmount - optionMax))
        )
  const totalCoins = Math.min(
    MAX_WALLET_COINS,
    Math.round(normalized * WALLET_EXTRA_RANGE) + MAX_BUNDLE_COINS
  )
  const silverCoins = Math.min(
    totalCoins - 1,
    Math.ceil(totalCoins * bonusPercent)
  )

  return { totalCoins, silverCoins }
}

/**
 * Calculate wagering progress percentage
 */
export function calculateWageringProgress(progress: {
  wagering_required_minor: number
  wagered_minor: number
  remaining_minor: number
}): number {
  // Guard against divide-by-zero and clamp to 0-100%
  const denominator = Math.max(1, progress.wagering_required_minor)
  const percentage = (progress.wagered_minor / denominator) * 100
  return Math.min(100, Math.max(0, Math.floor(percentage)))
}

/**
 * Extract bonus percentage from deposit option
 */
export function extractBonusPercent(option: DepositBonusOption): number {
  if (option.type === 'promotion') {
    const matchPercentage = option.promotion_details?.match_percentage

    if (matchPercentage !== undefined) {
      return Math.max(0, matchPercentage / 100)
    }
  }

  const multiplier =
    option.components?.[0]?.payload?.bonus_multiplier ??
    option.metadata?.bonus_multiplier ??
    1
  return multiplierToBonusPercent(multiplier)
}

/**
 * Get display amount for an option (max_deposit_minor for promotions, price_minor for others)
 */
export function getDisplayAmount(option: DepositBonusOption): number {
  if (option.type === 'promotion') {
    const defaultAmount = option.promotion_details?.default_deposit_minor
    if (defaultAmount !== undefined) {
      return defaultAmount
    }

    if (option.max_deposit_minor) {
      return option.max_deposit_minor
    }
  }

  return option.price_minor ?? 0
}

/**
 * Remove trailing ".00" from formatted currency strings.
 */
export function stripTrailingZeroCents(formatted: string): string {
  return formatted.replace(/([.,]00)(?!\d)/, '')
}
